import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../context/DarkModeContext';
import Footer from '../components/Footer';
import '../styles/MarkdownPreviewPage.css';

const DEFAULT_MARKDOWN = `# Welcome to Markdown Preview

## Features Overview

This editor supports **bold**, *italic*, and ***bold italic*** text.

You can also use ~~strikethrough~~ and \`inline code\`.

### Code Block

\`\`\`javascript
function greet(name) {
  return \`Hello, \${name}!\`;
}
console.log(greet('World'));
\`\`\`

### Blockquote

> "The best way to predict the future is to invent it."
> — Alan Kay

### Lists

Unordered list:
- Item one
- Item two
- Item three

Ordered list:
1. First step
2. Second step
3. Third step

### Links & Images

Visit [OpenAI](https://openai.com) for more info.

![Sample Image](https://via.placeholder.com/300x150)

---

That's all the features!
`;

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function parseMarkdown(text) {
  const lines = text.split('\n');
  let html = '';
  let i = 0;

  function parseInline(str) {
    // Images before links
    str = str.replace(/!\[([^\]]*)\]\(([^)]*)\)/g, '<img alt="$1" src="$2" />');
    // Links
    str = str.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    // Bold+Italic
    str = str.replace(/\*{3}(.+?)\*{3}/g, '<strong><em>$1</em></strong>');
    str = str.replace(/_{3}(.+?)_{3}/g, '<strong><em>$1</em></strong>');
    // Bold
    str = str.replace(/\*{2}(.+?)\*{2}/g, '<strong>$1</strong>');
    str = str.replace(/_{2}(.+?)_{2}/g, '<strong>$1</strong>');
    // Italic
    str = str.replace(/\*(.+?)\*/g, '<em>$1</em>');
    str = str.replace(/_(.+?)_/g, '<em>$1</em>');
    // Strikethrough
    str = str.replace(/~~(.+?)~~/g, '<del>$1</del>');
    // Inline code
    str = str.replace(/`([^`]+)`/g, '<code>$1</code>');
    return str;
  }

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (/^(`{3}|~{3})/.test(line)) {
      const fence = line.match(/^(`{3}|~{3})/)[1];
      const lang = line.slice(fence.length).trim();
      let codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith(fence)) {
        codeLines.push(escapeHtml(lines[i]));
        i++;
      }
      i++; // skip closing fence
      const langClass = lang ? ` class="language-${escapeHtml(lang)}"` : '';
      html += `<pre><code${langClass}>${codeLines.join('\n')}</code></pre>\n`;
      continue;
    }

    // Horizontal rule
    if (/^(\*{3,}|-{3,}|_{3,})\s*$/.test(line)) {
      html += '<hr />\n';
      i++;
      continue;
    }

    // Headers
    const headerMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      html += `<h${level}>${parseInline(headerMatch[2])}</h${level}>\n`;
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      let quoteLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      html += `<blockquote>${parseInline(quoteLines.join('\n'))}</blockquote>\n`;
      continue;
    }

    // Unordered list
    if (/^[-*+]\s/.test(line)) {
      let items = [];
      while (i < lines.length && /^[-*+]\s/.test(lines[i])) {
        items.push(`<li>${parseInline(lines[i].replace(/^[-*+]\s/, ''))}</li>`);
        i++;
      }
      html += `<ul>${items.join('')}</ul>\n`;
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      let items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(`<li>${parseInline(lines[i].replace(/^\d+\.\s/, ''))}</li>`);
        i++;
      }
      html += `<ol>${items.join('')}</ol>\n`;
      continue;
    }

    // Empty line
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Paragraph
    let paraLines = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,6}\s|>\s?|[-*+]\s|\d+\.\s|`{3}|~{3}|-{3,}|\*{3,})/.test(lines[i])) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      html += `<p>${parseInline(paraLines.join(' '))}</p>\n`;
    }
  }

  return html;
}

const TOOLBAR_ACTIONS = [
  { label: 'B', title: 'Bold', before: '**', after: '**', sample: 'bold text' },
  { label: 'I', title: 'Italic', before: '*', after: '*', sample: 'italic text' },
  { label: 'Code', title: 'Inline Code', before: '`', after: '`', sample: 'code' },
  { label: 'H1', title: 'Heading 1', before: '# ', after: '', sample: 'Heading' },
  { label: 'H2', title: 'Heading 2', before: '## ', after: '', sample: 'Heading' },
  { label: '> Quote', title: 'Blockquote', before: '> ', after: '', sample: 'quote text' },
  { label: '- List', title: 'Unordered List', before: '- ', after: '', sample: 'list item' },
  { label: '1. OList', title: 'Ordered List', before: '1. ', after: '', sample: 'list item' },
  { label: '--- HR', title: 'Horizontal Rule', before: '\n---\n', after: '', sample: '' },
  { label: '[Link]', title: 'Link', before: '[', after: '](url)', sample: 'link text' },
];

export default function MarkdownPreviewPage() {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [markdown, setMarkdown] = useState(DEFAULT_MARKDOWN);
  const [copyRawMsg, setCopyRawMsg] = useState('Copy Raw');
  const [copyHtmlMsg, setCopyHtmlMsg] = useState('Copy HTML');
  const textareaRef = useRef(null);

  const previewHtml = parseMarkdown(markdown);
  const lineCount = markdown.split('\n').length;

  const insertFormatting = useCallback((action) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = markdown.slice(start, end) || action.sample;
    const newText =
      markdown.slice(0, start) +
      action.before +
      selected +
      action.after +
      markdown.slice(end);
    setMarkdown(newText);
    setTimeout(() => {
      ta.focus();
      const newCursor = start + action.before.length + selected.length + action.after.length;
      ta.setSelectionRange(newCursor, newCursor);
    }, 0);
  }, [markdown]);

  const handleCopyRaw = () => {
    navigator.clipboard.writeText(markdown).then(() => {
      setCopyRawMsg('Copied!');
      setTimeout(() => setCopyRawMsg('Copy Raw'), 2000);
    });
  };

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(previewHtml).then(() => {
      setCopyHtmlMsg('Copied!');
      setTimeout(() => setCopyHtmlMsg('Copy HTML'), 2000);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'document.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setMarkdown('');
    setTimeout(() => textareaRef.current && textareaRef.current.focus(), 0);
  };

  return (
    <div
      className={`container ${darkMode ? 'dark-theme' : ''}`}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}
    >
      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 1000,
          color: '#fff',
          fontSize: '1rem',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: '#1976d2',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
      >
        {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
      </button>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 2, sm: 0 } }}>
        <img
          src="/favicon.png"
          alt="Home"
          onClick={() => navigate('/')}
          title="Go to Home"
          style={{ height: '60px', width: '60px', cursor: 'pointer', borderRadius: '8px', flexShrink: 0, transition: 'opacity 0.15s' }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: darkMode ? '#42a5f5' : '#1976d2', textAlign: { xs: 'left', sm: 'center' } }}>
          📄 Markdown Preview
        </Typography>
        <Box sx={{ width: { sm: '100px' } }} />
      </Box>

      {/* Toolbar */}
      <div className="md-toolbar">
        {TOOLBAR_ACTIONS.map((action) => (
          <button
            key={action.label}
            className="md-toolbar-btn"
            title={action.title}
            onClick={() => insertFormatting(action)}
          >
            {action.label}
          </button>
        ))}
        <button className="md-toolbar-btn" title="Clear" onClick={handleClear} style={{ color: '#d32f2f', marginLeft: 'auto' }}>
          ✕ Clear
        </button>
      </div>

      {/* Actions row */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <button className="md-action-btn" onClick={handleCopyRaw}>{copyRawMsg}</button>
        <button className="md-action-btn" onClick={handleCopyHtml}>{copyHtmlMsg}</button>
        <button className="md-action-btn" onClick={handleDownload}>⬇ Download .md</button>
      </div>

      {/* Panes */}
      <div className="markdown-panes" style={{ flex: 1 }}>
        {/* Editor */}
        <div className="md-pane">
          <div className="md-pane-header">
            <span>✏️ Editor</span>
            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>{lineCount} lines</span>
          </div>
          <textarea
            ref={textareaRef}
            className="md-editor-textarea"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            spellCheck={false}
            placeholder="Write your Markdown here..."
          />
        </div>

        {/* Preview */}
        <div className="md-pane">
          <div className="md-pane-header">
            <span>👁 Preview</span>
            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>Live render</span>
          </div>
          <div
            className="md-preview-body"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
}
